import React from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import {
  CALL_ADDITIONAL_DOCUMENT_TYPES,
  normalizeCallAdditionalDocumentType,
} from "@features/vessel-call/create/constants";
import { useTranslation } from "react-i18next";

interface AdditionalDocumentCardProps {
  documentName?: string;
  documentType?: string;
  otherSpecifyType?: string;
  documentSummary?: string;
  fileName?: string;
  url?: string;
  onDownload?: () => void;
  onPreview?: () => void;
  issueDate?: Date | string;
  expiryDate?: Date | string;
  testId?: string;
}

const formatDateToDDMMYYYY = (value: Date | string): string => {
  if (value instanceof Date) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    const day = String(value.getUTCDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  }

  const isoDateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoDateOnlyMatch) {
    const [, year, month, day] = isoDateOnlyMatch;
    return `${day}/${month}/${year}`;
  }

  return value;
};

const formatOptionalDate = (label: string, value?: Date | string) => {
  if (!value) return null;
  return `${label}: ${formatDateToDDMMYYYY(value)}`;
};

const getDocumentLabel = (title?: string, fileName?: string): string => {
  const normalizedFileName = fileName?.trim();

  if (normalizedFileName && normalizedFileName !== "." && normalizedFileName !== "-") {
    return normalizedFileName;
  }

  return title ?? "";
};

const CallAdditionalDocumentsCard: React.FC<AdditionalDocumentCardProps> = ({
  documentName,
  documentType,
  otherSpecifyType,
  documentSummary,
  fileName,
  onDownload,
  onPreview,
  issueDate,
  expiryDate,
  testId,
}) => {
  const { t } = useTranslation();
  const documentLabel = getDocumentLabel(documentName, fileName);
  const normalizedDocumentType = normalizeCallAdditionalDocumentType(documentType);
  const documentTypeLabelByValue: Record<string, string> = {
    [CALL_ADDITIONAL_DOCUMENT_TYPES.CERTIFICATE]: t(
      "vessel-call.additional-documents.document-types.certificate",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.PERMIT]: t(
      "vessel-call.additional-documents.document-types.permit",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.DOCUMENT]: t(
      "vessel-call.additional-documents.document-types.document",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.VESSEL_MASTER_SOF]: t(
      "vessel-call.additional-documents.document-types.vessel-master-sof",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.PRE_SAILING_MASTER_DECLARATION]: t(
      "vessel-call.additional-documents.document-types.pre-sailing-master-declaration",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.INSURANCE]: t(
      "vessel-call.additional-documents.document-types.insurance",
    ),
    [CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY]: t(
      "vessel-call.additional-documents.document-types.other-specify",
    ),
  };

  const documentTypeText = documentType
    ? (documentTypeLabelByValue[normalizedDocumentType] ?? documentType)
    : null;

  const issueDateText = formatOptionalDate(
    t("vessel-call.additional-documents.issue-date"),
    issueDate,
  );
  const expiryDateText = formatOptionalDate(
    t("vessel-call.additional-documents.expiry-date"),
    expiryDate,
  );

  return (
    <div data-testid={testId} className="border rounded p-4 w-full border-[#F1F2F4]">
      <h3 className="font-bold text-base mb-2">{documentName}</h3>
      <div className="flex justify-between items-center mb-3 w-full gap-3">
        <button
          type="button"
          className="text-blue-600 font-normal underline break-all cursor-pointer bg-transparent border-none p-0 text-left min-w-0"
          onClick={onPreview}
        >
          {documentLabel}
        </button>
        <button
          type="button"
          onClick={onDownload}
          className="shrink-0 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
          title="Download"
          aria-label="Download document"
        >
          <FileDownloadOutlined />
        </button>
      </div>
      <p className="text-sm text-[#646A77]">
        {documentTypeText && (
          <span className="block">
            {t("vessel-call.additional-documents.document-type")}: {documentTypeText}
            {normalizedDocumentType === CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY &&
            otherSpecifyType?.trim()
              ? ` - ${otherSpecifyType.trim()}`
              : ""}
          </span>
        )}
        {issueDateText && <span className="block">{issueDateText}</span>}
        {expiryDateText && <span className="block">{expiryDateText}</span>}
        {documentSummary && (
          <span className="block">
            {t("vessel-call.additional-documents.document-summary")}: {documentSummary}
          </span>
        )}
      </p>
    </div>
  );
};

export default CallAdditionalDocumentsCard;
